import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2,
  Inbox,
  Search,
  Sparkles,
  Paperclip,
  Mail,
  MailOpen,
  Reply,
  X,
  Tags as TagsIcon,
  CheckCheck,
  ArrowLeft,
  Tag as TagIcon,
  ArrowUp,
  ArrowDown,
  Users,
  Ban,
  Trash2,
  RefreshCw,
  Flag,
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFeed, type FeedItem } from '../../hooks/useFeed';
import { useBadges } from '../../hooks/useBadges';
import { useToReply } from '../../hooks/useToReply';
import { useTags } from '../../hooks/useTags';
import { useMailStore } from '../../store/mail';
import { useUiStore } from '../../store/ui';
import { messagesApi, tagsApi, deletionApi, accountsApi } from '../../lib/api';
import { MailViewer } from '../layout/MailViewer';
import { SlimSidebar } from '../layout/SlimSidebar';
import { HelpHint } from '../ui/HelpHint';

/**
 * Vista ELENCO "Cruscotto" (nuova home, stile kpi.unikai.it).
 * - Header con KPI cliccabili (totale, non letti, da rispondere) + filtri rapidi per categoria.
 * - Elenco mail ultimi 15 giorni in formato tabella (mittente, oggetto, data, categoria, tag).
 * - Hover riga -> anteprima riassunto AI. Click riga -> dettaglio (MailViewer a lato).
 * - Multi-selezione righe (checkbox) + barra azioni bulk (prima azione: "Da rispondere").
 * Palette applicata via wrapper .wm-list (indipendente dal tema globale dark/light).
 */
// ---- Larghezze colonne ridimensionabili (stile Excel), persistite in localStorage ----
// Ordine colonne visibili: Data / Mittente / Oggetto / Categoria-Tag.
// Tutte le colonne sono ridimensionabili con maniglia visibile sull'header.
const COLS_KEY = 'wm-list-cols';
// Colonne a larghezza fissa e ridimensionabili a mano (maniglia stile Excel).
// La colonna "Categoria / Tag" NON e' qui: e' elastica e assorbe lo spazio
// residuo, adattandosi in modo responsive alla larghezza del desktop.
type ColW = { date: number; sender: number; subject: number; actions: number };
const DEFAULT_COLS: ColW = { date: 64, sender: 288, subject: 320, actions: 220 };
const MIN_COLS: Record<keyof ColW, number> = { date: 48, sender: 90, subject: 110, actions: 150 };
// Larghezza minima della colonna elastica "Categoria / Tag": sotto questa soglia
// compare lo scroll orizzontale (su desktop stretti), sopra la colonna si allarga.
const CAT_MIN = 240;
function loadCols(): ColW {
  try {
    const r = JSON.parse(localStorage.getItem(COLS_KEY) || 'null');
    // Merge coi default: gestisce anche vecchie preferenze senza "subject".
    if (r && typeof r.sender === 'number') return { ...DEFAULT_COLS, ...r };
  } catch {
    /* localStorage non disponibile */
  }
  return DEFAULT_COLS;
}

// ---- Larghezza colonna DETTAGLIO (MailViewer) ridimensionabile, persistita ----
const DETAIL_KEY = 'wm-list-detailw';
const DETAIL_DEFAULT = 560;
const DETAIL_MIN = 380;
const DETAIL_MAX = 1000;
function loadDetailW(): number {
  try {
    const r = Number(localStorage.getItem(DETAIL_KEY));
    if (r >= DETAIL_MIN && r <= DETAIL_MAX) return r;
  } catch {
    /* ignora */
  }
  return DETAIL_DEFAULT;
}

// ---- Paginazione a blocchi (dimensione pagina fissa) ----
// Non ragioniamo piu' in "righe" ma in giorni (vedi Periodo). La pagina mostra
// sempre 25 mail; l'utente scorre lo storico con i pulsanti Avanti/Indietro.
const PAGE_SIZE = 25;

// ---- Filtro temporale rapido (persistito) ----
const TIME_KEY = 'wm-list-timerange';
type TimeRange = 'all' | 'today' | 'yesterday' | '7d' | '15d' | 'month' | 'prevMonth' | 'custom';
const TIME_OPTS: { key: TimeRange; label: string }[] = [
  { key: 'today', label: 'Oggi' },
  { key: 'yesterday', label: 'Ieri' },
  { key: '7d', label: 'Settimana' },
  { key: '15d', label: '15 giorni' },
  { key: 'month', label: 'Mese' },
  { key: 'prevMonth', label: 'Mese precedente' },
  { key: 'all', label: 'Tutte' },
];
const TIME_KEYS: TimeRange[] = ['all', 'today', 'yesterday', '7d', '15d', 'month', 'prevMonth', 'custom'];
function loadTimeRange(): TimeRange {
  try {
    const r = localStorage.getItem(TIME_KEY) as TimeRange | null;
    if (r && TIME_KEYS.includes(r)) return r;
  } catch {
    /* ignora */
  }
  // Primo accesso: default "15 giorni" (richiesta utente: ragioniamo a giorni,
  // non a righe; lo storico piu' ampio resta su richiesta).
  return '15d';
}

// Finestra di caricamento del feed necessaria per ogni periodo.
const DAYS_FOR_RANGE: Record<TimeRange, number> = {
  today: 7,
  yesterday: 7,
  '7d': 7,
  '15d': 15,
  month: 31,
  prevMonth: 62, // il mese di calendario precedente puo' distare fino a ~62 giorni
  all: 31,
  custom: 31, // range "da data a data": la finestra reale è calcolata dalle date scelte
};
function saveTimeRange(r: TimeRange) {
  try {
    localStorage.setItem(TIME_KEY, r);
  } catch {
    /* ignora */
  }
}

// ---- Range personalizzato "da data a data" (persistito) ----
const RANGE_KEY = 'wm-list-custom-range';
type CustomRange = { from: string; to: string }; // date 'YYYY-MM-DD' ('' = estremo aperto)
function loadCustomRange(): CustomRange {
  try {
    const r = JSON.parse(localStorage.getItem(RANGE_KEY) || 'null');
    if (r && typeof r.from === 'string' && typeof r.to === 'string') return r;
  } catch {
    /* ignora */
  }
  return { from: '', to: '' };
}
function saveCustomRange(r: CustomRange) {
  try {
    localStorage.setItem(RANGE_KEY, JSON.stringify(r));
  } catch {
    /* ignora */
  }
}
// Giorni di storico da caricare per coprire una data iniziale (clamp 1..365).
function daysSince(fromISO: string): number {
  if (!fromISO) return 365; // inizio aperto = tutto lo storico disponibile
  const from = new Date(fromISO + 'T00:00:00').getTime();
  const d = Math.ceil((Date.now() - from) / (24 * 60 * 60 * 1000)) + 1;
  return Math.min(365, Math.max(1, d));
}
// Finestra feed per il range custom: almeno 31 gg, fino a coprire la data iniziale.
function customFeedDays(fromISO: string): number {
  return Math.max(31, daysSince(fromISO));
}
// Intervallo [min,max) in millisecondi per il periodo scelto (null = tutte le mail caricate).
function timeBounds(r: TimeRange): { min: number; max: number } | null {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const day = 24 * 60 * 60 * 1000;
  switch (r) {
    case 'today':
      return { min: startToday, max: Infinity };
    case 'yesterday':
      return { min: startToday - day, max: startToday };
    case '7d':
      return { min: now.getTime() - 7 * day, max: Infinity };
    case '15d':
      return { min: now.getTime() - 15 * day, max: Infinity };
    case 'month':
      return { min: now.getTime() - 31 * day, max: Infinity };
    case 'prevMonth': {
      // Mese di calendario precedente: [1° del mese scorso, 1° di questo mese).
      const firstThis = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
      const firstPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
      return { min: firstPrev, max: firstThis };
    }
    default:
      return null;
  }
}

// ---- Ordinamento + raggruppamento (persistiti in localStorage) ----
const SORT_KEY = 'wm-list-sort-v2';
type SortKey = 'date' | 'sender' | 'subject';
type SortDir = 'asc' | 'desc';
type SortPref = { key: SortKey; dir: SortDir; group: boolean };
const DEFAULT_SORT: SortPref = { key: 'date', dir: 'desc', group: false };
function loadSort(): SortPref {
  try {
    const r = JSON.parse(localStorage.getItem(SORT_KEY) || 'null');
    if (r && r.key) return { ...DEFAULT_SORT, ...r };
  } catch {
    /* localStorage non disponibile */
  }
  return DEFAULT_SORT;
}
function saveSort(p: SortPref) {
  try {
    localStorage.setItem(SORT_KEY, JSON.stringify(p));
  } catch {
    /* ignora */
  }
}
function senderOf(it: FeedItem) {
  // from_name puo' essere stringa vuota: in tal caso ripiega sull'email.
  const name = (it.from_name ?? '').trim();
  const email = (it.from_email ?? '').trim();
  return name || email || '(sconosciuto)';
}

export function ListView() {
  // ---- Caricamento progressivo del feed ----
  // 1) 7 giorni SUBITO (render veloce anche su caselle con migliaia di msg);
  // 2) appena a video, estensione automatica a 15 giorni in background;
  // 3) finestra estesa (31 gg per Mese/Tutte, 62 gg per "Mese precedente")
  //    SOLO quando l'utente la chiede.
  const [wantFull, setWantFull] = useState(() => {
    const r = loadTimeRange();
    if (r === 'custom') return daysSince(loadCustomRange().from) > 15;
    return DAYS_FOR_RANGE[r] > 15;
  });
  // Giorni della finestra estesa: 31 default, 62 per "Mese precedente",
  // fino a 365 per un range "da data a data".
  const [fullDays, setFullDays] = useState(() => {
    const r = loadTimeRange();
    if (r === 'custom') return customFeedDays(loadCustomRange().from);
    return DAYS_FOR_RANGE[r] > 31 ? 62 : 31;
  });
  const feed7 = useFeed(7);
  const feed15 = useFeed(15, feed7.isSuccess);
  const feedFull = useFeed(fullDays, wantFull);
  const items = feedFull.data ?? feed15.data ?? feed7.data;
  const isLoading = feed7.isLoading && !items;
  // Finestra effettivamente caricata + flag "sto estendendo".
  const loadedDays = feedFull.data ? fullDays : feed15.data ? 15 : feed7.data ? 7 : 0;
  const extendingFeed = wantFull ? feedFull.isFetching : feed15.isFetching;
  const { data: toReply } = useToReply();
  const { data: allTags } = useTags();
  // Badge disponibili (Alert/Da rispondere + custom) per il picker delle righe.
  const { data: badgeList } = useBadges();
  const feedFilter = useMailStore((s) => s.feedFilter);
  const setFeedFilter = useMailStore((s) => s.setFeedFilter);
  const selectedIds = useMailStore((s) => s.selectedIds);
  const toggleSelected = useMailStore((s) => s.toggleSelected);
  const setSelectedMany = useMailStore((s) => s.setSelectedMany);
  const clearSelected = useMailStore((s) => s.clearSelected);
  const selectedMessageId = useMailStore((s) => s.selectedMessageId);
  const setSelectedMessage = useMailStore((s) => s.setSelectedMessage);
  const openSettings = useUiStore((s) => s.openSettings);
  const setCurrentView = useUiStore((s) => s.setCurrentView);
  const pendingMailFilter = useUiStore((s) => s.pendingMailFilter);
  const setPendingMailFilter = useUiStore((s) => s.setPendingMailFilter);

  const queryClient = useQueryClient();

  // Larghezze colonne (drag-resize stile Excel, salvate nel browser).
  const [cols, setCols] = useState<ColW>(loadCols);
  // Filtro temporale rapido (persistito) + pagina corrente (non persistita).
  const [page, setPage] = useState(0);
  const [timeRange, setTimeRange] = useState<TimeRange>(loadTimeRange);
  // Range personalizzato "da data a data" (attivo quando timeRange === 'custom').
  const [customRange, setCustomRange] = useState<CustomRange>(loadCustomRange);
  // Larghezza della colonna di dettaglio della mail (ridimensionabile, persistita).
  const [detailWidth, setDetailWidth] = useState<number>(loadDetailW);
  // Ricerca testuale (mittente/oggetto/categoria/tag), non persistita.
  const [search, setSearch] = useState('');

  // Filtro "in attesa" impostato dal Cruscotto (card categoria / chip tag / spam):
  // al mount lo applica ai chip esistenti (feedFilter) e lo azzera. Per i tag
  // servono nome e colore, quindi attende il caricamento di allTags.
  useEffect(() => {
    if (!pendingMailFilter) return;
    if (pendingMailFilter.kind === 'category') {
      setFeedFilter({ kind: 'category', category: pendingMailFilter.value });
    } else if (pendingMailFilter.kind === 'spam') {
      setFeedFilter({ kind: 'spam' });
    } else {
      if (!allTags) return; // riprova quando i tag sono disponibili
      const t = allTags.find((x) => x.id === pendingMailFilter.value);
      if (t) setFeedFilter({ kind: 'tag', tags: [{ tagId: t.id, label: t.name, color: t.color }] });
    }
    setPage(0);
    setPendingMailFilter(null);
  }, [pendingMailFilter, allTags, setFeedFilter, setPendingMailFilter]);
  // Con un filtro tag attivo carica la finestra completa (fino a 31 giorni)
  // così il tag mostra più mail possibili, a prescindere dal periodo scelto.
  useEffect(() => {
    if (feedFilter.kind === 'tag') setWantFull(true);
  }, [feedFilter.kind]);
  const resizing = useRef<{ key: keyof ColW; startX: number; startW: number } | null>(null);
  // dir: 1 = maniglia sul bordo destro (default), -1 = maniglia sul bordo
  // sinistro (colonna Azioni, ancorata a destra: trascinare a sinistra allarga).
  function startResize(e: React.MouseEvent, key: keyof ColW, dir: 1 | -1 = 1) {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = { key, startX: e.clientX, startW: cols[key] };
    const move = (ev: MouseEvent) => {
      const r = resizing.current;
      if (!r) return;
      const w = Math.max(MIN_COLS[r.key], r.startW + dir * (ev.clientX - r.startX));
      setCols((c) => ({ ...c, [r.key]: w }));
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      resizing.current = null;
      setCols((c) => {
        try {
          localStorage.setItem(COLS_KEY, JSON.stringify(c));
        } catch {
          /* ignora errori di persistenza */
        }
        return c;
      });
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }

  // Ridimensionamento della colonna di dettaglio della mail (trascina a sinistra per allargare).
  const detailResizing = useRef<{ startX: number; startW: number } | null>(null);
  function startDetailResize(e: React.MouseEvent) {
    e.preventDefault();
    detailResizing.current = { startX: e.clientX, startW: detailWidth };
    const move = (ev: MouseEvent) => {
      const r = detailResizing.current;
      if (!r) return;
      const w = Math.min(Math.max(DETAIL_MIN, r.startW - (ev.clientX - r.startX)), DETAIL_MAX);
      setDetailWidth(w);
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      detailResizing.current = null;
      setDetailWidth((w) => {
        try {
          localStorage.setItem(DETAIL_KEY, String(w));
        } catch {
          /* ignora */
        }
        return w;
      });
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }

  // Ordinamento + raggruppa per mittente (persistiti).
  const [sort, setSort] = useState<SortPref>(loadSort);
  function toggleSort(key: SortKey) {
    setPage(0);
    setSort((s0) => {
      const next: SortPref =
        s0.key === key
          ? { ...s0, dir: s0.dir === 'asc' ? 'desc' : 'asc' }
          : { ...s0, key, dir: key === 'date' ? 'desc' : 'asc' };
      saveSort(next);
      return next;
    });
  }
  function toggleGroup() {
    setPage(0);
    setSort((s0) => {
      // Spegnendo il raggruppamento si torna al default: Data decrescente.
      const next = s0.group ? { ...DEFAULT_SORT } : { ...s0, group: true };
      saveSort(next);
      return next;
    });
  }
  // Cambia il filtro attivo (tag/categoria/da rispondere) e torna alla prima pagina.
  // Selezione MULTIPLA dei tag (OR): un click aggiunge/toglie il tag dal filtro.
  const toggleTagFilter = (t: { id: string; name: string; color: string }) => {
    const cur = feedFilter.kind === 'tag' ? feedFilter.tags : [];
    const next = cur.some((x) => x.tagId === t.id)
      ? cur.filter((x) => x.tagId !== t.id)
      : [...cur, { tagId: t.id, label: t.name, color: t.color }];
    pickFilter(next.length === 0 ? { kind: 'none' } : { kind: 'tag', tags: next });
  };

  const pickFilter = (f: Parameters<typeof setFeedFilter>[0]) => {
    setPage(0);
    setFeedFilter(f);
  };
  // Cambia il periodo temporale (persistito) e torna alla prima pagina.
  const pickTime = (r: TimeRange) => {
    setPage(0);
    setTimeRange(r);
    saveTimeRange(r);
    // Periodi oltre i 15 giorni: carica la finestra estesa su richiesta.
    // "Mese precedente" richiede fino a 62 giorni di storico.
    setFullDays(DAYS_FOR_RANGE[r] > 31 ? 62 : 31);
    if (DAYS_FOR_RANGE[r] > 15) setWantFull(true);
  };
  // Applica un range "da data a data": imposta timeRange='custom' e la finestra feed.
  const pickCustom = (from: string, to: string) => {
    const cr = { from, to };
    setCustomRange(cr);
    saveCustomRange(cr);
    setTimeRange('custom');
    saveTimeRange('custom');
    setPage(0);
    setFullDays(customFeedDays(from));
    setWantFull(daysSince(from) > 15);
  };
  // onChange degli input data: se entrambe vuote torna al preset "15 giorni".
  const applyCustom = (from: string, to: string) => {
    if (!from && !to) pickTime('15d');
    else pickCustom(from, to);
  };

  // Set degli id "da rispondere" per filtro e per marcare le righe.
  const toReplyIds = useMemo(() => new Set((toReply ?? []).map((m) => m.id)), [toReply]);

  // Categorie AI presenti nelle mail (per i filtri rapidi).
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const it of items ?? []) if (it.ai_category) set.add(it.ai_category);
    return Array.from(set).sort();
  }, [items]);

  // Numero di mail spam nel dataset (per la chip filtro Spam).
  const spamCount = useMemo(() => (items ?? []).filter((it) => it.is_spam).length, [items]);

  // Tag presenti nelle mail caricate (ricerca rapida per tag): si aggiorna
  // automaticamente quando un tag viene aggiunto o rimosso da una mail.
  const feedTags = useMemo(() => {
    const map = new Map<string, { id: string; name: string; color: string; count: number }>();
    for (const it of items ?? [])
      for (const t of it.tags) {
        const e = map.get(t.id);
        if (e) e.count += 1;
        else map.set(t.id, { id: t.id, name: t.name, color: t.color, count: 1 });
      }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name, 'it'));
  }, [items]);

  // Applica: filtro temporale + filtro attivo (tag/categoria/da rispondere) + ricerca testuale.
  const filtered = useMemo<FeedItem[]>(() => {
    let base = items ?? [];
    // Con un filtro TAG attivo mostriamo TUTTE le mail di quel tag, senza
    // distinzione di periodo: il filtro Periodo viene ignorato.
    const tagActive = feedFilter.kind === 'tag';
    let b = tagActive ? null : timeBounds(timeRange);
    // Range "da data a data": limiti calcolati dalle date scelte (estremi aperti ammessi).
    if (!tagActive && timeRange === 'custom') {
      const min = customRange.from ? new Date(customRange.from + 'T00:00:00').getTime() : -Infinity;
      const max = customRange.to ? new Date(customRange.to + 'T23:59:59.999').getTime() : Infinity;
      b = { min, max };
    }
    if (b)
      base = base.filter((it) => {
        if (!it.date) return false;
        const t = new Date(it.date).getTime();
        return t >= b.min && t < b.max;
      });
    if (feedFilter.kind === 'tag')
      // AND: la mail deve avere TUTTI i tag selezionati (non solo almeno uno).
      // `every` sui tag scelti → passa solo chi li possiede tutti.
      base = base.filter((it) => feedFilter.tags.every((sel) => it.tags.some((t) => t.id === sel.tagId)));
    else if (feedFilter.kind === 'category')
      base = base.filter((it) => it.ai_category === feedFilter.category);
    else if (feedFilter.kind === 'toReply') base = base.filter((it) => toReplyIds.has(it.id));
    else if (feedFilter.kind === 'spam') base = base.filter((it) => it.is_spam);
    else if (feedFilter.kind === 'noSpam') base = base.filter((it) => !it.is_spam);
    const q = search.trim().toLowerCase();
    if (q)
      base = base.filter(
        (it) =>
          (it.from_name ?? '').toLowerCase().includes(q) ||
          (it.from_email ?? '').toLowerCase().includes(q) ||
          (it.subject ?? '').toLowerCase().includes(q) ||
          (it.ai_category ?? '').toLowerCase().includes(q) ||
          (it.ai_summary ?? '').toLowerCase().includes(q) ||
          (it.snippet ?? '').toLowerCase().includes(q) ||
          it.tags.some((t) => t.name.toLowerCase().includes(q)),
      );
    return base;
  }, [items, feedFilter, toReplyIds, timeRange, customRange, search]);

  // Ordinamento applicato alle righe filtrate.
  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sort.dir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av: string;
      let bv: string;
      if (sort.key === 'sender') {
        av = senderOf(a).toLowerCase();
        bv = senderOf(b).toLowerCase();
      } else if (sort.key === 'subject') {
        av = (a.subject ?? '').toLowerCase();
        bv = (b.subject ?? '').toLowerCase();
      } else {
        av = a.date ?? '';
        bv = b.date ?? '';
      }
      return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
    });
    return arr;
  }, [filtered, sort.key, sort.dir]);

  // Paginazione a blocchi fissi (25 mail per pagina). Slice sulle righe ordinate.
  const totalRows = sorted.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
  const curPage = Math.min(page, pageCount - 1);
  const pageStart = curPage * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, totalRows);
  const paged = useMemo(() => sorted.slice(pageStart, pageStart + PAGE_SIZE), [sorted, pageStart]);

  // Righe di render: piatte oppure raggruppate per mittente (solo sulla pagina corrente).
  type Entry = { type: 'group'; label: string; count: number; ids: string[] } | { type: 'row'; item: FeedItem };
  const entries = useMemo<Entry[]>(() => {
    if (!sort.group) return paged.map((it) => ({ type: 'row', item: it }));
    const groups = new Map<string, FeedItem[]>();
    for (const it of paged) {
      const k = senderOf(it);
      const arr = groups.get(k) ?? [];
      arr.push(it);
      groups.set(k, arr);
    }
    const keys = Array.from(groups.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    const out: Entry[] = [];
    for (const k of keys) {
      const its = groups.get(k)!;
      // Dentro il gruppo: sempre data decrescente (la piu' recente in alto).
      its.sort((a, b) => ((a.date ?? '') < (b.date ?? '') ? 1 : -1));
      // ids delle mail del gruppo: servono per la checkbox "seleziona tutto il gruppo".
      out.push({ type: 'group', label: k, count: its.length, ids: its.map((it) => it.id) });
      for (const it of its) out.push({ type: 'row', item: it });
    }
    return out;
  }, [paged, sort.group]);

  // KPI header.
  const total = items?.length ?? 0;
  const unseen = (items ?? []).filter((it) => !it.is_seen).length;
  const toReplyCount = toReplyIds.size;

  // Selezione visibile (id filtrati attualmente selezionati).
  const filteredIds = filtered.map((it) => it.id);
  const allVisibleSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));
  const selectedCount = filteredIds.filter((id) => selectedIds.has(id)).length;

  // Mutazione bulk "da rispondere".
  const bulkMutation = useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: 'todo' | 'done' | null }) =>
      messagesApi.bulkReplyStatus(ids, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['to-reply'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      clearSelected();
    },
  });

  // Mutazione bulk: applica un tag alla selezione.
  const bulkTagMutation = useMutation({
    mutationFn: ({ ids, tagId }: { ids: string[]; tagId: string }) =>
      messagesApi.bulkAddTag(ids, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['message'] });
      clearSelected();
    },
  });

  // Mutazione bulk: blocca i mittenti della selezione (blocklist + spam).
  const bulkBlockMutation = useMutation({
    mutationFn: (ids: string[]) => messagesApi.bulkBlockSender(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      clearSelected();
    },
  });
  // Azioni per-riga: aggiungi/crea tag (per nome) e stato "da rispondere / risposto".
  const rowTagMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => messagesApi.addTag(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['message'] });
    },
  });
  const rowReplyMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'todo' | 'done' | null }) =>
      messagesApi.setReplyStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['to-reply'] });
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });
  // Rimozione di un tag dalla riga (× sul chip).
  const rowRemoveTagMutation = useMutation({
    mutationFn: ({ id, tagId, scope }: { id: string; tagId: string; scope: 'message' | 'sender' | 'domain' }) =>
      messagesApi.removeTagScoped(id, tagId, scope),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['message'] });
    },
  });
  // Badge per-riga: assegna/rimuovi (copia/rimuove anche la copia IMAP).
  const rowAddBadgeMutation = useMutation({
    mutationFn: ({ id, badgeId }: { id: string; badgeId: string }) =>
      messagesApi.addBadge(id, { badgeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['message'] });
      queryClient.invalidateQueries({ queryKey: ['badges'] });
    },
  });
  const rowRemoveBadgeMutation = useMutation({
    mutationFn: ({ id, badgeId }: { id: string; badgeId: string }) =>
      messagesApi.removeBadge(id, badgeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['message'] });
      queryClient.invalidateQueries({ queryKey: ['badges'] });
    },
  });
  // Togli dallo spam con ambito: 'message' = solo questa mail,
  // 'sender'/'domain' = mittente/dominio SEMPRE attendibile (allowlist)
  // + pulizia retroattiva di tutte le mail corrispondenti (flag + tag spam).
  const rowUnspamMutation = useMutation({
    mutationFn: async ({ id, scope }: { id: string; scope: 'message' | 'sender' | 'domain' }) => {
      await messagesApi.unspamScoped(id, scope);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['message'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
  // Regola tag ereditaria: mittente/dominio/contenuto -> tutte le mail
  // corrispondenti (presenti e future) ereditano il tag.
  const tagRuleMutation = useMutation({
    mutationFn: async (input: { kind: 'sender' | 'domain' | 'content'; pattern: string; name: string }) => {
      await tagsApi.createRule(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
  // Bulk: crea regole tag ereditarie (mittente/dominio) per la selezione.
  // Una regola per ogni mittente/dominio distinto: tutte le mail corrispondenti
  // (presenti e future) erediteranno il tag.
  const bulkRuleMutation = useMutation({
    mutationFn: async (
      rules: { kind: 'sender' | 'domain'; pattern: string; tagId: string }[],
    ) => {
      for (const r of rules) await tagsApi.createRule(r);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['message'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      clearSelected();
    },
  });
  const tagNames = useMemo(() => (allTags ?? []).map((t) => t.name), [allTags]);

  // Ambito della "Applica tag" bulk: solo le mail selezionate oppure una
  // regola ereditaria per mittente/dominio.
  const [bulkTagScope, setBulkTagScope] = useState<'message' | 'sender' | 'domain'>('message');

  const applyTag = (tagId: string) => {
    const items = filtered.filter((it) => selectedIds.has(it.id));
    if (items.length === 0 || !tagId) return;

    // Ambito "solo questa selezione": comportamento classico (tag diretto).
    if (bulkTagScope === 'message') {
      bulkTagMutation.mutate({ ids: items.map((it) => it.id), tagId });
      return;
    }

    // Email mittente normalizzate della selezione.
    const emails = items
      .map((it) => (it.from_email ?? '').toLowerCase().trim())
      .filter((e) => e.includes('@'));

    if (bulkTagScope === 'sender') {
      const distinct = [...new Set(emails)];
      if (distinct.length === 0) return;
      if (
        distinct.length > 1 &&
        !window.confirm(
          `Creare una regola tag per ${distinct.length} mittenti distinti?\n` +
            `Tutte le mail (presenti e future) di questi mittenti erediteranno il tag.`,
        )
      )
        return;
      bulkRuleMutation.mutate(
        distinct.map((pattern) => ({ kind: 'sender' as const, pattern, tagId })),
      );
      return;
    }

    // bulkTagScope === 'domain'
    const domains = [...new Set(emails.map((e) => e.split('@')[1]).filter(Boolean))];
    if (domains.length === 0) return;
    if (
      domains.length > 1 &&
      !window.confirm(
        `Creare una regola tag per ${domains.length} domini distinti?\n` +
          `Tutte le mail (presenti e future) di questi domini erediteranno il tag.`,
      )
    )
      return;
    bulkRuleMutation.mutate(
      domains.map((pattern) => ({ kind: 'domain' as const, pattern, tagId })),
    );
  };

  const applyBlockSender = () => {
    const ids = filteredIds.filter((id) => selectedIds.has(id));
    if (ids.length === 0) return;
    if (!window.confirm(`Bloccare i mittenti di ${ids.length} messaggi e segnarli come spam?`))
      return;
    bulkBlockMutation.mutate(ids);
  };

  const applyBulk = (status: 'todo' | null) => {
    const ids = filteredIds.filter((id) => selectedIds.has(id));
    if (ids.length === 0) return;
    bulkMutation.mutate({ ids, status });
  };

  // Cestina (parcheggio 48h): reversibile dalla vista "In cancellazione".
  // Invio A BLOCCHI da 50 cosi' la barra di avanzamento mostra il progresso reale.
  const [deleteProgress, setDeleteProgress] = useState<{ done: number; total: number } | null>(null);
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const CHUNK = 50;
      setDeleteProgress({ done: 0, total: ids.length });
      for (let i = 0; i < ids.length; i += CHUNK) {
        await deletionApi.mark(ids.slice(i, i + CHUNK));
        setDeleteProgress({ done: Math.min(i + CHUNK, ids.length), total: ids.length });
      }
    },
    onSuccess: () => {
      clearSelected();
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['deletion-alert'] });
    },
    onSettled: () => setDeleteProgress(null),
  });
  // Bulk: marca le selezionate come spam (senza blocklist mittente).
  const bulkSpamMutation = useMutation({
    mutationFn: (ids: string[]) => messagesApi.bulkSpam(ids),
    onSuccess: () => {
      clearSelected();
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['tags'] });
    },
  });
  // Sync manuale IMAP: forza la sincronizzazione degli account e ricarica l'elenco.
  const syncMutation = useMutation({
    mutationFn: () => accountsApi.syncNow(),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      queryClient.invalidateQueries({ queryKey: ['messages'] });
      queryClient.invalidateQueries({ queryKey: ['folders'] });
    },
  });

  const applyBulkSpam = () => {
    const ids = filteredIds.filter((id) => selectedIds.has(id));
    if (ids.length === 0) return;
    bulkSpamMutation.mutate(ids);
  };

  const applyBulkDelete = () => {
    const ids = filteredIds.filter((id) => selectedIds.has(id));
    if (ids.length === 0) return;
    bulkDeleteMutation.mutate(ids);
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Sidebar collassabile (navigazione viste + tag + logout) */}
      <SlimSidebar />
      {/* Area elenco */}
      <div className="wm-list flex flex-1 flex-col overflow-hidden">
        {/* ---------- Header KPI + filtri rapidi ---------- */}
        <div className="border-b px-5 pb-3 pt-4" style={{ borderColor: 'var(--border)' }}>
          <div className="mb-3 flex items-center gap-2">
            <Sparkles size={16} style={{ color: 'var(--accent)' }} />
            <h1 className="text-[15px] font-semibold" style={{ color: 'var(--accent-dark)' }}>
              Cruscotto posta
            </h1>
            {/* Aiuto contestuale della sezione Posta */}
            <HelpHint topic="list" />
            <span className="text-xs" style={{ color: 'var(--muted)' }}>
              {extendingFeed
                ? `· caricati ${loadedDays} gg, estendo lo storico…`
                : `· ultimi ${loadedDays || 7} giorni caricati`}
            </span>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-3 gap-3">
            <KpiCard
              icon={<Mail size={16} />}
              label="Totale"
              value={total}
              active={feedFilter.kind === 'none'}
              onClick={() => pickFilter({ kind: 'none' })}
            />
            <KpiCard
              icon={<MailOpen size={16} />}
              label="Non letti"
              value={unseen}
              tone="warn"
            />
            <KpiCard
              icon={<Reply size={16} />}
              label="Da rispondere"
              value={toReplyCount}
              tone="ko"
              active={feedFilter.kind === 'toReply'}
              onClick={() =>
                pickFilter(feedFilter.kind === 'toReply' ? { kind: 'none' } : { kind: 'toReply' })
              }
            />
          </div>

          {/* Riga STATI: Tutte / Da rispondere / Spam / No-spam / Cancellate.
              Le categorie AI non sono piu' chips qui: doppiavano i tag. */}
          {(
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <button
                className={'wm-chip px-2.5 py-1 text-[11px] font-medium' + (feedFilter.kind === 'none' ? ' is-active' : '')}
                onClick={() => pickFilter({ kind: 'none' })}
              >
                Tutte
              </button>
              <button
                className={'wm-chip px-2.5 py-1 text-[11px] font-medium' + (feedFilter.kind === 'toReply' ? ' is-active' : '')}
                onClick={() => pickFilter({ kind: 'toReply' })}
              >
                Da rispondere
              </button>
              {spamCount > 0 && (
                <button
                  className={'wm-chip wm-chip-spam px-2.5 py-1 text-[11px] font-medium' + (feedFilter.kind === 'spam' ? ' is-active' : '')}
                  onClick={() =>
                    pickFilter(feedFilter.kind === 'spam' ? { kind: 'none' } : { kind: 'spam' })
                  }
                  title="Mostra solo le mail spam"
                >
                  <Ban size={11} className="mr-1 inline" /> Spam ({spamCount})
                </button>
              )}
              {/* No-spam: esclude dall'elenco le mail marcate spam */}
              <button
                className={'wm-chip px-2.5 py-1 text-[11px] font-medium' + (feedFilter.kind === 'noSpam' ? ' is-active' : '')}
                onClick={() =>
                  pickFilter(feedFilter.kind === 'noSpam' ? { kind: 'none' } : { kind: 'noSpam' })
                }
                title="Nascondi le mail spam dall'elenco"
              >
                No-spam
              </button>
              {/* Cancellate: apre la vista "In cancellazione" (parcheggio 48h) */}
              <button
                className="wm-chip px-2.5 py-1 text-[11px] font-medium"
                onClick={() => setCurrentView('deletion')}
                title="Vai alle mail in cancellazione (parcheggio 48h)"
              >
                <Trash2 size={11} className="mr-1 inline" /> Cancellate
              </button>
            </div>
          )}

          {/* Ricerca rapida per tag: chips dei tag presenti nelle mail caricate.
              Si aggiorna da sola quando un tag viene aggiunto o rimosso. */}
          {feedTags.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span
                className="mr-1 text-[11px] font-semibold uppercase tracking-wide"
                style={{ color: 'var(--muted)' }}
              >
                Tag
              </span>
              {feedTags.map((t) => (
                <button
                  key={t.id}
                  className={
                    'wm-chip wm-chip-tag px-3 py-1 font-semibold' +
                    (feedFilter.kind === 'tag' && feedFilter.tags.some((x) => x.tagId === t.id)
                      ? ' is-active'
                      : '')
                  }
                  onClick={() => toggleTagFilter({ id: t.id, name: t.name, color: t.color })}
                  title={'Filtra per tag "' + t.name + '" (click su piu\' tag = combinazione)'}
                >
                  {t.name} ({t.count})
                </button>
              ))}
            </div>
          )}

          {/* Filtro temporale rapido (Periodo) */}
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span
              className="mr-1 text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: 'var(--muted)' }}
            >
              Periodo
            </span>
            {TIME_OPTS.map((o) => (
              <button
                key={o.key}
                disabled={feedFilter.kind === 'tag'}
                title={
                  feedFilter.kind === 'tag'
                    ? 'Con un tag selezionato il periodo è ignorato: vedi tutte le mail del tag'
                    : undefined
                }
                className={
                  'wm-chip px-2.5 py-1 text-[11px] font-medium' +
                  (timeRange === o.key ? ' is-active' : '') +
                  (feedFilter.kind === 'tag' ? ' opacity-40 cursor-not-allowed' : '')
                }
                onClick={() => pickTime(o.key)}
              >
                {o.label}
              </button>
            ))}
            {feedFilter.kind === 'tag' && (
              <span className="text-[11px] italic" style={{ color: 'var(--muted)' }}>
                periodo ignorato · mostro tutte le mail del tag
              </span>
            )}

            {/* Selettore "da data a data" (range personalizzato) */}
            <span className="mx-0.5 text-[11px]" style={{ color: 'var(--muted)' }} aria-hidden>
              |
            </span>
            <input
              type="date"
              value={customRange.from}
              max={customRange.to || undefined}
              disabled={feedFilter.kind === 'tag'}
              onChange={(e) => applyCustom(e.target.value, customRange.to)}
              className={
                'wm-chip px-2 py-0.5 text-[11px] font-medium' +
                (timeRange === 'custom' ? ' is-active' : '') +
                (feedFilter.kind === 'tag' ? ' opacity-40 cursor-not-allowed' : '')
              }
              style={{ colorScheme: 'dark' }}
              title="Da questa data"
              aria-label="Da data"
            />
            <span className="text-[11px]" style={{ color: 'var(--muted)' }} aria-hidden>
              →
            </span>
            <input
              type="date"
              value={customRange.to}
              min={customRange.from || undefined}
              disabled={feedFilter.kind === 'tag'}
              onChange={(e) => applyCustom(customRange.from, e.target.value)}
              className={
                'wm-chip px-2 py-0.5 text-[11px] font-medium' +
                (timeRange === 'custom' ? ' is-active' : '') +
                (feedFilter.kind === 'tag' ? ' opacity-40 cursor-not-allowed' : '')
              }
              style={{ colorScheme: 'dark' }}
              title="A questa data"
              aria-label="A data"
            />
            {timeRange === 'custom' && customRange.from && daysSince(customRange.from) >= 365 && (
              <span
                className="text-[11px] italic"
                style={{ color: 'var(--accent-dark)' }}
                title="Il feed carica al massimo gli ultimi 365 giorni: le mail più vecchie non sono disponibili qui."
              >
                ⚠ solo ultimi 365 giorni
              </span>
            )}

            {/* Ricerca + azioni (spostate qui, in coda alla riga Periodo) */}
            <div className="relative ml-auto">
              <Search
                size={13}
                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--muted)' }}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                placeholder="Cerca: mittente, oggetto, tag…"
                className="wm-search"
                aria-label="Cerca nelle mail"
              />
              {search && (
                <button
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5"
                  style={{ color: 'var(--muted)' }}
                  onClick={() => {
                    setSearch('');
                    setPage(0);
                  }}
                  title="Cancella ricerca"
                >
                  <X size={13} />
                </button>
              )}
            </div>
            <button
              className={'wm-btn' + (sort.group ? ' wm-btn-primary' : '')}
              onClick={toggleGroup}
              title="Raggruppa le mail per mittente"
            >
              <Users size={14} /> Raggruppa per mittente
            </button>
            <button
              className="wm-btn"
              disabled={syncMutation.isPending}
              title="Sincronizza adesso la posta dal server"
              onClick={() => syncMutation.mutate()}
            >
              <RefreshCw size={14} className={syncMutation.isPending ? 'animate-spin' : ''} />
              {syncMutation.isPending ? 'Sincronizzo…' : 'Sync'}
            </button>
            <button className="wm-btn" onClick={() => openSettings('tags')}>
              <TagsIcon size={14} /> Gestione tag
            </button>
          </div>
        </div>

        {/* ---------- Barra paginazione (25/pagina, navigazione Avanti/Indietro) ---------- */}
        <div
          className="flex flex-wrap items-center gap-1.5 border-b px-5 py-2"
          style={{ borderColor: 'var(--border)' }}
        >
          <span
            className="mr-1 text-[11px] font-semibold uppercase tracking-wide"
            style={{ color: 'var(--muted)' }}
          >
            Pagina
          </span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px]" style={{ color: 'var(--muted)' }}>
              {totalRows === 0 ? '0' : String(pageStart + 1) + '–' + String(pageEnd)} di {totalRows}
            </span>
            <button
              className="wm-btn px-2 py-1"
              disabled={curPage <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              title="Pagina precedente"
            >
              ‹
            </button>
            <span className="text-[11px] font-semibold" style={{ color: 'var(--text)' }}>
              {curPage + 1}/{pageCount}
            </span>
            <button
              className="wm-btn px-2 py-1"
              disabled={curPage >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              title="Pagina successiva"
            >
              ›
            </button>
          </div>
        </div>

        {/* ---------- Tabella elenco ---------- */}
        {/* NB: niente padding-top qui: lo sticky dell'header si aggancia al bordo
            del contenitore scroll e un padding-top lascerebbe una fascia in cui le
            righe scorrono VISIBILI sopra l'intestazione. */}
        <div className="relative flex-1 overflow-auto px-5 pb-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={18} className="animate-spin" style={{ color: 'var(--muted)' }} />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              filtered={feedFilter.kind !== 'none' || search.trim() !== ''}
              onClear={() => {
                pickFilter({ kind: 'none' });
                setSearch('');
              }}
            />
          ) : (
            <table
              className="wm-table border-separate"
              style={{
                borderSpacing: 0,
                tableLayout: 'fixed',
                // Riempie sempre la larghezza disponibile (responsive): la colonna
                // "Categoria / Tag" e' elastica e assorbe lo spazio in eccesso.
                width: '100%',
                // Sotto questa soglia (colonne fisse + minimo di Categoria/Tag)
                // compare lo scroll orizzontale.
                minWidth: 40 + cols.date + cols.sender + cols.subject + cols.actions + CAT_MIN,
              }}
            >
              <thead>
                {/* NB: niente position:relative inline sui th: sovrascriverebbe lo
                    sticky del CSS (.wm-table thead th) e l'intestazione sparirebbe
                    allo scroll. Lo sticky fa gia' da riferimento per .wm-col-resize. */}
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      aria-label="Seleziona tutte"
                      checked={allVisibleSelected}
                      onChange={(e) => setSelectedMany(filteredIds, e.target.checked)}
                    />
                  </th>
                  {/* Data: prima colonna visibile (richiesta utente) */}
                  <th style={{ width: cols.date }}>
                    <SortLabel label="Data" col="date" sort={sort} onSort={toggleSort} />
                    <span className="wm-col-resize" onMouseDown={(e) => startResize(e, 'date')} />
                  </th>
                  <th style={{ width: cols.sender }}>
                    <SortLabel label="Mittente" col="sender" sort={sort} onSort={toggleSort} />
                    <span className="wm-col-resize" onMouseDown={(e) => startResize(e, 'sender')} />
                  </th>
                  <th style={{ width: cols.subject }}>
                    <SortLabel label="Oggetto" col="subject" sort={sort} onSort={toggleSort} />
                    <span className="wm-col-resize" onMouseDown={(e) => startResize(e, 'subject')} />
                  </th>
                  {/* Colonna elastica: assorbe lo spazio residuo e si adatta in modo
                      responsive alla larghezza del desktop (min via .wm-col-cat). */}
                  <th className="wm-col-cat">Categoria / Tag</th>
                  {/* Colonna Azioni: fissa e ridimensionabile a mano (ultima colonna). */}
                  <th style={{ width: cols.actions }}>
                    Azioni
                    <span
                      className="wm-col-resize wm-col-resize--left"
                      onMouseDown={(e) => startResize(e, 'actions', -1)}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) =>
                  e.type === 'group' ? (
                    <tr key={'g-' + e.label}>
                      {/* Checkbox: seleziona/deseleziona tutte le mail del gruppo. */}
                      <td className="wm-group">
                        <input
                          type="checkbox"
                          aria-label={`Seleziona tutte le mail di ${e.label}`}
                          checked={e.ids.length > 0 && e.ids.every((id) => selectedIds.has(id))}
                          onChange={(ev) => setSelectedMany(e.ids, ev.target.checked)}
                        />
                      </td>
                      <td colSpan={5} className="wm-group">
                        <span className="wm-group-inner">
                          <Users size={12} /> {e.label}
                          <span className="wm-group-count">{e.count}</span>
                        </span>
                      </td>
                    </tr>
                  ) : (
                    <Row
                      key={e.item.id}
                      item={e.item}
                      active={selectedMessageId === e.item.id}
                      selected={selectedIds.has(e.item.id)}
                      isToReply={toReplyIds.has(e.item.id)}
                      onToggle={() => toggleSelected(e.item.id)}
                      onOpen={() => setSelectedMessage(e.item.id)}
                      onTag={(tagId, label, color) => pickFilter({ kind: 'tag', tags: [{ tagId, label, color }] })}
                      onAddTag={(name) => rowTagMutation.mutate({ id: e.item.id, name })}
                      onRemoveTag={(tagId, scope) => rowRemoveTagMutation.mutate({ id: e.item.id, tagId, scope })}
                      onUnspam={(scope) => rowUnspamMutation.mutate({ id: e.item.id, scope })}
                      onAddRule={(kind, pattern, name) => tagRuleMutation.mutate({ kind, pattern, name })}
                      onSetReply={(status) => rowReplyMutation.mutate({ id: e.item.id, status })}
                      badges={badgeList ?? []}
                      onAddBadge={(badgeId) => rowAddBadgeMutation.mutate({ id: e.item.id, badgeId })}
                      onRemoveBadge={(badgeId) => rowRemoveBadgeMutation.mutate({ id: e.item.id, badgeId })}
                      onFilterBadge={(badgeId, label, color) =>
                        pickFilter({ kind: 'tag', tags: [{ tagId: badgeId, label, color }] })
                      }
                      tagNames={tagNames}
                    />
                  ),
                )}
              </tbody>
            </table>
          )}

          {/* ---------- Barra azioni bulk ---------- */}
          {selectedCount > 0 && (
            <div className="pointer-events-none sticky bottom-0 left-0 right-0 z-20 flex justify-center pb-2 pt-4">
              <div className="wm-bulkbar pointer-events-auto flex items-center gap-2 px-3 py-2">
                <CheckCheck size={15} />
                <span className="text-xs font-semibold">{selectedCount} selezionate</span>
                <span className="mx-1 opacity-40">|</span>
                <button
                  className="wm-btn"
                  disabled={bulkMutation.isPending}
                  onClick={() => applyBulk('todo')}
                >
                  <Reply size={13} /> Da rispondere
                </button>
                <button
                  className="wm-btn"
                  disabled={bulkMutation.isPending}
                  onClick={() => applyBulk(null)}
                  title="Rimuovi stato 'da rispondere'"
                >
                  Rimuovi
                </button>
                <span className="mx-1 opacity-40">|</span>
                <div className="flex items-center gap-1.5 text-white">
                  <TagIcon size={14} />
                  {/* Ambito: solo la selezione, oppure regola ereditaria mittente/dominio */}
                  <select
                    className="wm-btn"
                    style={{ color: 'var(--text)' }}
                    value={bulkTagScope}
                    onChange={(e) =>
                      setBulkTagScope(e.target.value as 'message' | 'sender' | 'domain')
                    }
                    title="A cosa applicare il tag: solo alle mail selezionate oppure come regola per mittente/dominio (anche future)"
                  >
                    <option value="message">Selezione</option>
                    <option value="sender">Mittente</option>
                    <option value="domain">Dominio</option>
                  </select>
                  <select
                    className="wm-btn"
                    style={{ color: 'var(--text)' }}
                    disabled={bulkTagMutation.isPending || bulkRuleMutation.isPending}
                    value=""
                    onChange={(e) => {
                      applyTag(e.target.value);
                      e.currentTarget.selectedIndex = 0;
                    }}
                    title="Applica un tag alla selezione"
                  >
                    <option value="">Applica tag…</option>
                    {(allTags ?? []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <span className="mx-1 opacity-40">|</span>
                <button
                  className="wm-btn"
                  disabled={bulkSpamMutation.isPending}
                  onClick={applyBulkSpam}
                  title="Segna le mail selezionate come spam (senza bloccare il mittente)"
                >
                  <Ban size={13} /> Segna spam
                </button>
                <button
                  className="wm-btn"
                  disabled={bulkBlockMutation.isPending}
                  onClick={applyBlockSender}
                  title="Blocca i mittenti selezionati e segnali come spam"
                >
                  <Ban size={13} /> Blocca mittente
                </button>
                <button
                  className="wm-btn"
                  disabled={bulkDeleteMutation.isPending}
                  onClick={applyBulkDelete}
                  title="Cestina: parcheggio 48h, annullabile da 'In cancellazione'"
                >
                  <Trash2 size={13} /> Cestina (48h)
                </button>
                {/* Avanzamento cancellazione massiva (invio a blocchi da 50) */}
                {deleteProgress && (
                  <span className="flex items-center gap-2 text-xs font-semibold">
                    <span
                      className="h-1.5 w-28 overflow-hidden rounded-full"
                      style={{ background: 'rgb(255 255 255 / 0.25)' }}
                    >
                      <span
                        className="block h-full rounded-full transition-all duration-200"
                        style={{
                          width: `${Math.round((deleteProgress.done / Math.max(1, deleteProgress.total)) * 100)}%`,
                          background: '#fff',
                        }}
                      />
                    </span>
                    {deleteProgress.done}/{deleteProgress.total}
                  </span>
                )}
                <button
                  className="rounded-md p-1 text-white/80 transition hover:bg-white/10"
                  onClick={clearSelected}
                  title="Deseleziona"
                >
                  <X size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dettaglio messaggio (appare solo quando una riga è aperta) */}
      {selectedMessageId && (
        <aside
          className="wm-list relative flex shrink-0 flex-col border-l"
          style={{ borderColor: 'var(--border)', width: detailWidth }}
        >
          {/* Maniglia per ridimensionare la colonna di dettaglio (trascina a sinistra per allargare) */}
          <div
            className="wm-detail-resize"
            onMouseDown={startDetailResize}
            role="separator"
            aria-orientation="vertical"
            aria-label="Ridimensiona dettaglio"
            title="Trascina per ridimensionare il dettaglio"
          />
          {/* Barra con chiusura del dettaglio (torna all'elenco a piena larghezza) */}
          <div
            className="flex h-9 shrink-0 items-center justify-between border-b px-3"
            style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
          >
            <button className="wm-btn" onClick={() => setSelectedMessage(null)} title="Chiudi (Esc)">
              <ArrowLeft size={14} /> Torna all'elenco
            </button>
            <button
              onClick={() => setSelectedMessage(null)}
              title="Chiudi"
              className="rounded-md p-1"
              style={{ color: 'var(--muted)' }}
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex flex-1 overflow-hidden">
            <MailViewer />
          </div>
        </aside>
      )}
    </div>
  );
}

function SortLabel({
  label,
  col,
  sort,
  onSort,
}: {
  label: string;
  col: SortKey;
  sort: SortPref;
  onSort: (k: SortKey) => void;
}) {
  const active = sort.key === col;
  return (
    <button
      type="button"
      onClick={() => onSort(col)}
      className="inline-flex items-center gap-1 text-inherit"
      title={`Ordina per ${label}`}
    >
      {label}
      {active &&
        (sort.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
    </button>
  );
}

function KpiCard({
  icon,
  label,
  value,
  tone,
  active,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone?: 'warn' | 'ko';
  active?: boolean;
  onClick?: () => void;
}) {
  const valueColor =
    tone === 'ko' ? 'var(--ko)' : tone === 'warn' ? 'var(--warn)' : 'var(--accent-dark)';
  return (
    <div
      className={'wm-kpi flex items-center gap-3 px-4 py-3' + (onClick ? ' is-clickable' : '') + (active ? ' is-active' : '')}
      onClick={onClick}
    >
      <div
        className="flex h-9 w-9 items-center justify-center rounded-lg"
        style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
      >
        {icon}
      </div>
      <div>
        <div className="text-[22px] font-bold leading-none" style={{ color: valueColor }}>
          {value}
        </div>
        <div className="mt-1 text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--muted)' }}>
          {label}
        </div>
      </div>
    </div>
  );
}

function Row({
  item,
  active,
  selected,
  isToReply,
  onToggle,
  onOpen,
  onTag,
  onAddTag,
  onRemoveTag,
  onUnspam,
  onAddRule,
  onSetReply,
  badges,
  onAddBadge,
  onRemoveBadge,
  onFilterBadge,
  tagNames,
}: {
  item: FeedItem;
  active: boolean;
  selected: boolean;
  isToReply: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onTag: (tagId: string, label: string, color: string) => void;
  onAddTag: (name: string) => void;
  onRemoveTag: (tagId: string, scope: 'message' | 'sender' | 'domain') => void;
  onUnspam: (scope: 'message' | 'sender' | 'domain') => void;
  onAddRule: (kind: 'sender' | 'domain' | 'content', pattern: string, name: string) => void;
  onSetReply: (status: 'todo' | 'done' | null) => void;
  badges: { id: string; name: string; color: string }[];
  onAddBadge: (badgeId: string) => void;
  onRemoveBadge: (badgeId: string) => void;
  onFilterBadge: (badgeId: string, label: string, color: string) => void;
  tagNames: string[];
}) {
  // Se il nome mittente e' vuoto/assente, mostra l'email.
  const sender = (item.from_name ?? '').trim() || (item.from_email ?? '').trim() || '(sconosciuto)';
  const preview = item.ai_summary ?? item.snippet ?? null;
  // Il tag "spam" e' rappresentato dal chip spam dedicato (con × per toglierlo):
  // lo separo dagli altri tag per non mostrarlo due volte.
  const spamTag = item.tags.find((t) => t.name.toLowerCase() === 'spam');
  const normalTags = item.tags.filter(
    (t) => t.name.toLowerCase() !== 'spam' && t.kind !== 'badge',
  );
  // Badge assegnati al messaggio (pallini colorati) + set id per il picker.
  const assignedBadges = item.tags.filter((t) => t.kind === 'badge');
  const assignedBadgeIds = new Set(assignedBadges.map((b) => b.id));
  // Pannellino "aggiungi tag" della riga (stato locale) + ambito della regola.
  const [tagOpen, setTagOpen] = useState(false);
  const [tagText, setTagText] = useState('');
  // Ambito tag: mail = solo questa; sender/domain/content = regola ereditaria.
  const [tagScope, setTagScope] = useState<'mail' | 'sender' | 'domain' | 'content'>('mail');
  const [tagPattern, setTagPattern] = useState('');
  // Menu "togli dallo spam": scelta ambito (solo questa / mittente / dominio).
  const [unspamOpen, setUnspamOpen] = useState(false);
  // Menu ambito per la RIMOZIONE tag: id del tag di cui si sta scegliendo l'ambito.
  const [removeTagFor, setRemoveTagFor] = useState<string | null>(null);
  // Menu picker badge della riga (bandierina).
  const [badgeMenuOpen, setBadgeMenuOpen] = useState(false);
  const fromEmail = (item.from_email ?? '').toLowerCase().trim();
  const fromDomain = fromEmail.includes('@') ? fromEmail.split('@')[1] : '';
  const submitTag = () => {
    const name = tagText.trim();
    if (name) {
      if (tagScope === 'mail') {
        onAddTag(name);
      } else if (tagScope === 'sender' && fromEmail) {
        onAddRule('sender', fromEmail, name);
      } else if (tagScope === 'domain' && fromDomain) {
        onAddRule('domain', fromDomain, name);
      } else if (tagScope === 'content') {
        const pat = tagPattern.trim() || (item.subject ?? '').trim();
        if (pat) onAddRule('content', pat, name);
      }
    }
    setTagText('');
    setTagPattern('');
    setTagScope('mail');
    setTagOpen(false);
  };
  return (
    <tr
      className={
        'wm-row group' +
        (active ? ' is-active' : '') +
        (selected ? ' is-selected' : '') +
        (!item.is_seen ? ' is-unseen' : '') +
        (item.is_spam ? ' is-spam' : '')
      }
      style={item.is_spam ? { color: 'var(--ko)' } : undefined}
    >
      {/* Checkbox */}
      <td onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={onToggle} aria-label="Seleziona" />
      </td>

      {/* Data (prima colonna visibile) */}
      <td onClick={onOpen} className="whitespace-nowrap" style={{ color: 'var(--muted)' }}>
        {item.date ? format(new Date(item.date), 'dd MMM', { locale: it }) : ''}
      </td>

      {/* Mittente */}
      <td onClick={onOpen}>
        <div className="flex min-w-0 items-center gap-1.5">
          {!item.is_seen && (
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: 'var(--accent)' }} />
          )}
          {isToReply && <Reply size={12} className="shrink-0" style={{ color: 'var(--ko)' }} />}
          <span className="truncate">{sender}</span>
        </div>
      </td>

      {/* Oggetto + anteprima hover */}
      <td onClick={onOpen} className="relative">
        <div className="flex min-w-0 items-center gap-1.5">
          {item.has_attachments && <Paperclip size={12} className="shrink-0" style={{ color: 'var(--muted)' }} />}
          <span className="truncate">{item.subject ?? '(senza oggetto)'}</span>
        </div>
        {preview && (
          <div className="pointer-events-none absolute left-0 top-full z-30 mt-1 w-[380px] max-w-[90vw] opacity-0 transition-opacity duration-150 group-hover:opacity-100">
            <div className="wm-preview p-3">
              <div
                className="mb-1 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider"
                style={{ color: 'var(--accent)' }}
              >
                <Sparkles size={9} /> {item.ai_summary ? 'Riassunto AI' : 'Anteprima'}
              </div>
              <p className="line-clamp-4 text-[12px] leading-relaxed" style={{ color: 'var(--text)' }}>
                {preview}
              </p>
            </div>
          </div>
        )}
      </td>

      {/* Categoria + tag (colonna elastica, responsive) */}
      <td onClick={onOpen} className="wm-col-cat">
        <div className="flex flex-wrap items-center gap-1">
          {/* Badge (pallini colorati): il pallino filtra per badge. */}
          {assignedBadges.map((b) => (
            <button
              key={b.id}
              type="button"
              title={`Badge ${b.name} — filtra`}
              onClick={(e) => {
                e.stopPropagation();
                onFilterBadge(b.id, b.name, b.color);
              }}
              style={{
                width: 12,
                height: 12,
                borderRadius: '9999px',
                backgroundColor: b.color,
                border: '1px solid rgba(0,0,0,0.25)',
                flex: '0 0 auto',
                cursor: 'pointer',
              }}
            />
          ))}
          {/* Picker badge: bandierina che apre l'elenco badge (toggle assegna/togli). */}
          <span className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="wm-badge-x"
              title="Badge…"
              onClick={() => setBadgeMenuOpen((v) => !v)}
              style={{ color: 'var(--muted)' }}
            >
              <Flag size={11} />
            </button>
            {badgeMenuOpen && (
              <span
                className="absolute left-0 top-full z-40 mt-1 flex flex-col gap-1 whitespace-nowrap rounded-lg border p-1.5 shadow-xl"
                style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
              >
                {badges.length === 0 && (
                  <span className="px-1 text-xs" style={{ color: 'var(--muted)' }}>
                    Nessun badge
                  </span>
                )}
                {badges.map((b) => {
                  const on = assignedBadgeIds.has(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      className="flex items-center gap-2 rounded px-1.5 py-0.5 text-xs hover:opacity-80"
                      style={{ color: 'var(--fg)' }}
                      onClick={() => {
                        if (on) onRemoveBadge(b.id);
                        else onAddBadge(b.id);
                        setBadgeMenuOpen(false);
                      }}
                    >
                      <span
                        className="rounded-full"
                        style={{ width: 10, height: 10, backgroundColor: b.color }}
                      />
                      {b.name}
                      {on && <CheckCheck size={11} />}
                    </button>
                  );
                })}
              </span>
            )}
          </span>
          {item.ai_category && <span className="wm-badge wm-badge-cat">{item.ai_category}</span>}
          {/* Chip spam dedicato: × toglie la mail dallo spam (flag + tag). */}
          {(item.is_spam || spamTag) && !unspamOpen && (
            <span className="wm-badge wm-badge-tag wm-badge-spam">
              <span className="wm-badge-label" style={{ cursor: 'default' }}>
                <Ban size={10} /> spam
              </span>
              <button
                type="button"
                className="wm-badge-x"
                title="Togli dallo spam…"
                onClick={(e) => {
                  e.stopPropagation();
                  setUnspamOpen(true);
                }}
              >
                <X size={10} />
              </button>
            </span>
          )}
          {/* Menu ambito "togli dallo spam": solo questa / mittente / dominio attendibile */}
          {(item.is_spam || spamTag) && unspamOpen && (
            <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <button
                className="wm-abtn"
                title="Togli dallo spam solo questa mail"
                onClick={() => {
                  setUnspamOpen(false);
                  onUnspam('message');
                }}
              >
                Solo questa
              </button>
              {fromEmail && (
                <button
                  className="wm-abtn wm-abtn-ok"
                  title={`Mittente sempre attendibile: ${fromEmail} (esce tutto dallo spam)`}
                  onClick={() => {
                    setUnspamOpen(false);
                    onUnspam('sender');
                  }}
                >
                  Mittente ✓
                </button>
              )}
              {fromDomain && (
                <button
                  className="wm-abtn wm-abtn-ok"
                  title={`Dominio sempre attendibile: @${fromDomain} (esce tutto dallo spam)`}
                  onClick={() => {
                    setUnspamOpen(false);
                    onUnspam('domain');
                  }}
                >
                  Dominio ✓
                </button>
              )}
              <button className="wm-abtn" title="Annulla" onClick={() => setUnspamOpen(false)}>
                <X size={10} />
              </button>
            </span>
          )}
          {/* Chip tag: la label filtra, la × rimuove il tag dalla mail. */}
          {normalTags.map((t) => (
            /* Wrapper relative FUORI dal chip: il chip ha overflow:hidden (per il
               pill arrotondato) e un popup absolute al suo interno verrebbe
               clippato → invisibile e non cliccabile (bug "tag non cancellabile"). */
            <span key={t.id} className="relative inline-flex">
            <span
              className="wm-badge wm-badge-tag"
              style={{ backgroundColor: `${t.color}22`, color: t.color }}
            >
              <button
                type="button"
                className="wm-badge-label"
                title={`Filtra per ${t.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onTag(t.id, t.name, t.color);
                }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: t.color }} />
                {t.name}
              </button>
              <button
                type="button"
                className="wm-badge-x"
                title={`Rimuovi tag ${t.name}…`}
                onClick={(e) => {
                  e.stopPropagation();
                  setRemoveTagFor(removeTagFor === t.id ? null : t.id);
                }}
              >
                <X size={10} />
              </button>
            </span>
              {removeTagFor === t.id && (
                <span
                  className="absolute right-0 top-full z-40 mt-1 flex items-center gap-1 whitespace-nowrap rounded-lg border p-1.5 shadow-xl"
                  style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="wm-abtn"
                    title="Rimuovi il tag solo da questa mail"
                    onClick={() => {
                      onRemoveTag(t.id, 'message');
                      setRemoveTagFor(null);
                    }}
                  >
                    solo questa
                  </button>
                  {fromEmail && (
                    <button
                      className="wm-abtn"
                      title={`Rimuovi il tag da tutte le mail di ${fromEmail} (e dalla regola mittente)`}
                      onClick={() => {
                        onRemoveTag(t.id, 'sender');
                        setRemoveTagFor(null);
                      }}
                    >
                      mittente
                    </button>
                  )}
                  {fromDomain && (
                    <button
                      className="wm-abtn"
                      title={`Rimuovi il tag da tutte le mail di @${fromDomain} (e dalla regola dominio)`}
                      onClick={() => {
                        onRemoveTag(t.id, 'domain');
                        setRemoveTagFor(null);
                      }}
                    >
                      dominio
                    </button>
                  )}
                  <button
                    className="wm-badge-x"
                    title="Annulla"
                    onClick={() => setRemoveTagFor(null)}
                  >
                    <X size={10} />
                  </button>
                </span>
              )}
            </span>
          ))}
        </div>
      </td>
      {/* Colonna azioni per-riga (compaiono al passaggio del mouse) */}
      <td className="wm-actions">
        <div className={'wm-actions-inner' + (tagOpen ? ' is-open' : '')}>
          {tagOpen ? (
            <span className="wm-tagadd" onClick={(e) => e.stopPropagation()}>
              {/* Ambito: questa mail oppure regola ereditaria (mittente/dominio/contenuto) */}
              <select
                className="wm-tagadd-input"
                style={{ width: 92 }}
                value={tagScope}
                title="A cosa applicare il tag: tutte le mail corrispondenti lo erediteranno"
                onChange={(e) =>
                  setTagScope(e.target.value as 'mail' | 'sender' | 'domain' | 'content')
                }
              >
                <option value="mail">Questa mail</option>
                {fromEmail && <option value="sender">Mittente</option>}
                {fromDomain && <option value="domain">Dominio</option>}
                <option value="content">Contenuto</option>
              </select>
              {tagScope === 'content' && (
                <input
                  className="wm-tagadd-input"
                  style={{ width: 110 }}
                  value={tagPattern}
                  placeholder="Testo da cercare…"
                  title="Le mail il cui oggetto o contenuto contiene questo testo erediteranno il tag"
                  onChange={(e) => setTagPattern(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <input
                className="wm-tagadd-input"
                list={'tags-' + item.id}
                value={tagText}
                autoFocus
                placeholder="Tag…"
                onChange={(e) => setTagText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitTag();
                  if (e.key === 'Escape') {
                    setTagText('');
                    setTagOpen(false);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <datalist id={'tags-' + item.id}>
                {tagNames.map((n) => (
                  <option key={n} value={n} />
                ))}
              </datalist>
              <button
                className="wm-abtn"
                title="Aggiungi"
                onClick={(e) => {
                  e.stopPropagation();
                  submitTag();
                }}
              >
                OK
              </button>
            </span>
          ) : (
            <button
              className="wm-abtn"
              title="Aggiungi o crea un tag"
              onClick={(e) => {
                e.stopPropagation();
                setTagOpen(true);
              }}
            >
              <TagIcon size={12} /> Tag
            </button>
          )}
          {isToReply ? (
            <button
              className="wm-abtn wm-abtn-ok"
              title="Segna come risposto"
              onClick={(e) => {
                e.stopPropagation();
                onSetReply('done');
              }}
            >
              <CheckCheck size={12} /> Risposto
            </button>
          ) : (
            <button
              className="wm-abtn wm-abtn-ko"
              title="Segna come da rispondere"
              onClick={(e) => {
                e.stopPropagation();
                onSetReply('todo');
              }}
            >
              <Reply size={12} /> Da rispondere
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function EmptyState({ filtered, onClear }: { filtered?: boolean; onClear?: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-8 py-20 text-center">
      <div
        className="mb-4 flex h-12 w-12 items-center justify-center rounded-full"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
      >
        <Inbox size={20} style={{ color: 'var(--muted)' }} />
      </div>
      <h3 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
        {filtered ? 'Nessuna mail per questo filtro' : 'Nessuna mail recente'}
      </h3>
      <p className="mt-1 max-w-xs text-xs" style={{ color: 'var(--muted)' }}>
        {filtered
          ? 'Nessun messaggio degli ultimi 15 giorni corrisponde al filtro selezionato.'
          : 'Le nuove mail appariranno qui con riassunto e tag generati dall’AI.'}
      </p>
      {filtered && (
        <button className="wm-btn mt-3" onClick={onClear}>
          Rimuovi filtro
        </button>
      )}
    </div>
  );
}
